<script setup>
import { toRefs, computed, inject } from 'vue'
import { useCartStore } from '@/stores/CartStore'
import { useCommonStore } from '@/stores/CommonStore'
import { XMarkIcon as XIcon } from '@heroicons/vue/24/outline'

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'

const siteConfig = inject('siteConfig')

const props = defineProps({
  classes: {
    type: String, default: ''
  }
})

const { classes } = toRefs(props)

const cartStore = useCartStore()
const commonStore = useCommonStore()

const openedCart = computed(() => cartStore.openedCart)

const guestTickets = computed(() => !!siteConfig.features?.events?.guestTickets)

const showCartItemBadge = computed(
  () => !siteConfig.features?.hideCartItemBadge && cartStore.items.length > 0
)

// with guest tickets, same event session + tier share one row with a quantity control
const cartRows = computed(() => {
  const rows = []
  const ticketRows = {}
  cartStore.items.forEach((item) => {
    if (guestTickets.value && item.type === 'ticket') {
      const key = `${item.eventId}:${item.scheduleId}:${item.tierName}`
      if (ticketRows[key]) {
        ticketRows[key].tickets.push(item)
        return
      }
      ticketRows[key] = { item, tickets: [item] }
      rows.push(ticketRows[key])
    } else {
      rows.push({ item, tickets: null })
    }
  })
  return rows
})

const rowCost = (item, tickets) =>
  (tickets || [item]).reduce(
    (sum, rowItem) =>
      sum +
      parseFloat(
        rowItem.effectiveCost !== undefined ? rowItem.effectiveCost : rowItem.nonMemberCost
      ),
    0
  )

const setTicketQty = (item, qty) => cartStore.setTicketQty({ ticket: item }, qty)

const removeRow = (item, tickets) => {
  (tickets || [item]).forEach((ticket) => cartStore.removeItem(ticket.cartId))
}

const openCart = () => {
  cartStore.toggleCart(true)
}

const outsideCloseCart = (event) => {
  if (commonStore.windowInnerWidth < 1280) return
  // Keep the cart open while adjusting ticket qty on event pages (guestTickets)
  if (event?.target?.closest?.('.ticket-qty')) return
  cartStore.toggleCart(false)
}

const closeCart = () => {
  cartStore.toggleCart(false)
}
</script>

<template>
  <Popover class="cart-modal">
    <PopoverButton class="cart-btn" :class="classes" @click="openCart">
      <span class="cart-btn-label">
        <Icon icon="ant-design:shopping-cart-outlined" class="icon" />
        <span
          v-if="showCartItemBadge"
          class="cart-badge"
          :aria-label="`${cartStore.items.length} items in cart`"
        >{{ cartStore.items.length }}</span>
      </span>
      Cart
    </PopoverButton>
    <transition name="translate">
      <PopoverPanel
        class="cart-popup"
        v-show="openedCart"
        v-click-outside="outsideCloseCart"
        static
      >
        <div class="container">
          <div>
            <h3 class="xlarge-text xl:large-regular">Your cart</h3>
            <button type="button" @click="closeCart" aria-label="Close cart">
              <XIcon class="icon" aria-hidden="true" />
            </button>
          </div>

          <div class="in-cart-classes">
            <ul v-if="cartStore.cartNotices.length" class="cart-notices small-regular" role="status">
              <li v-for="notice in cartStore.cartNotices" :key="notice">{{ notice }}</li>
            </ul>
            <template v-if="cartRows.length">
              <div
                class="class-box"
                v-for="({ item, tickets }, index) in cartRows"
                :key="tickets ? `${item.eventId}-${item.scheduleId}-${item.tierName}` : item.cartId || index"
              >
                <img
                  class="class-img"
                  :src="item.imageUrl"
                  :alt="item.title ? `${item.title} image` : 'Cart item image'"
                />
                <div class="info">
                  <h4 v-html="item.title" class="small-bold"></h4>
                  <p v-html="item.subtitle" class="small-regular"></p>
                  <strong class="small-bold block">
                    &dollar;{{ rowCost(item, tickets).toFixed(2) }}
                  </strong>
                  <template v-if="item.discountNote">
                    &nbsp;<small class="text-xs italic text-orange-600" v-html="item.discountNote"></small>
                  </template>
                  <p v-if="item.type === 'account-payment' && item.items" class="class-instructor small-regular">
                    <template v-for="accountItem in item.items" :key="accountItem.name">
                      {{ accountItem.name }} – ${{ accountItem.amountDue }}<br>
                    </template>
                  </p>
                  <div v-if="tickets" class="ticket-qty">
                    <button
                      type="button"
                      class="qty-btn btn-accent-1"
                      :aria-label="`Remove one ${item.tierName} ticket`"
                      @click="setTicketQty(item, tickets.length - 1)"
                    >&minus;</button>
                    <span class="qty-value" aria-live="polite">{{ tickets.length }}</span>
                    <button
                      type="button"
                      class="qty-btn btn-accent-1"
                      :disabled="!cartStore.canAddTicketFromCart(item)"
                      :aria-label="`Add one ${item.tierName} ticket`"
                      @click="setTicketQty(item, tickets.length + 1)"
                    >+</button>
                  </div>
                </div>
                <button
                  v-if="item.removable !== false"
                  class="btn-remove"
                  @click="removeRow(item, tickets)"
                  :aria-label="`Remove ${item.title || 'item'} from cart`"
                >
                  <img src="@/assets/icons/trash.svg" alt="" aria-hidden="true" />
                </button>
              </div>
            </template>
            <div class="empty-cart" v-else>
              <h3 class="heading-3">Empty Cart</h3>
            </div>
          </div>

          <div class="cart-footer body-regular">
            <div class="row" v-if="cartStore.taxes">
              <span>Tax</span>
              <span>&dollar;{{ cartStore.taxes.toFixed(2) }}</span>
            </div>
            <div class="row" v-if="siteConfig.modules.shop && !cartStore.shouldPickupItems && cartStore.shipping">
              <span>Shipping</span>
              <span>&dollar;{{ cartStore.shipping.toFixed(2) }}</span>
            </div>
            <div class="row">
              <strong>{{ cartStore.items.length }}
                {{
                  !cartStore.items.length || cartStore.items.length > 1
                    ? 'items'
                    : 'item'
                }}</strong>
              <strong>
                Total:
                <span class="total-amount">${{ cartStore.total.toFixed(2) }}</span>
              </strong>
            </div>

            <div class="actions">
              <router-link
                :to="{ name: 'Checkout: Review Cart' }"
                class="checkout-link btn-accent-1"
                :class="{ disabled: !cartStore.items.length }"
                @click="closeCart"
              >
                Checkout
              </router-link>
              <button class="outlined-accent-1 btn" @click="closeCart">
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>
</template>

<style lang="pcss" scoped>
.cart-btn-label {
  @apply relative inline-flex;
}
.cart-badge {
  @apply absolute -top-2 -left-2 min-w-[1.15rem] h-[1.15rem] px-1 rounded-full
    bg-accent-1 text-white text-[0.65rem] leading-none font-bold
    flex items-center justify-center;
}
.ticket-qty {
  @apply flex items-center gap-2 mt-3;
  .qty-btn {
    @apply w-8 h-8 p-0 flex items-center justify-center text-sm;
    &:disabled {
      @apply opacity-40 cursor-not-allowed;
    }
  }
  .qty-value {
    @apply min-w-[1.25rem] text-center font-bold text-sm;
  }
}
</style>
